import { useState, useCallback } from "react";
import { useParams } from "react-router-dom";

import useJStyles from "$hooks/useJStyles";
import useShippingRecord from "$hooks/useShippingRecord";
import { DeliveryType, ShippingRecord, ShippingRecordStatus } from "$types";

import ShippingDeliveryDatesBlock from "$components/ShippingRecord/ShippingDeliveryDatesBlock";
import ShippingLocationInfoBlock from "$components/ShippingRecord/ShippingLocationInfoBlock";
import ShippingParcelInfoBlock from "$components/ShippingRecord/ShippingParcelInfoBlock";
import ShippingRecordInfoBlock from "$components/ShippingRecord/ShippingRecordInfoBlock";
import ShippingRecordDisputeTableBlock from "$components/ShippingRecord/ShippingRecordDisputeTableBlock";
import ShippingRecordHistoryPopin from "$components/ShippingRecord/ShippingRecordHistoryPopin";
import ShippingRecordFileUploadBlock from "$components/ShippingRecord/ShippingRecordFileUploadBlock";
import ShippingRecordDeliveryTrackingBlock from "$components/ShippingRecord/ShippingRecordDeliveryTrackingBlock";

import { useStyles } from "./ShippingRecordDetails.styles";
import { HeaderShippingRecordsDetails } from "./HeaderShippingRecordsDetails";
import { Layout, Modal, Text } from "$components";
import { updateShippingRecordAndOp } from "$services/Op.service";
import { ShippingRecordService } from "$services";
import { queryClient } from "$utils/queryClient";
import { CacheInvalidationAction } from "$utils/cache";

function ShippingRecordDetails() {
  const { shippingRecordId } = useParams<any>();

  const {
    shippingRecord,
    fetchShippingRecord,
    updateShippingRecord,
    isFetchingShippingRecord,
  } = useShippingRecord(shippingRecordId);

  const [isHistoryDrawerOpen, toggleHistoryDrawer] = useState<boolean>(false);
  const [isCancelShippingRecordModalOpen, toggleCancelShippingRecordModal] =
    useState<boolean>(false);

  const isShippingRecordCanceled =
    shippingRecord?.status === ShippingRecordStatus.CANCELED;

  const { jclasses } = useJStyles();
  const classes = useStyles();

  const handleShippingRecordUpdate = async (
    values: Partial<ShippingRecord>
  ) => {
    if (isShippingRecordCanceled) return;
    await updateShippingRecordAndOp(shippingRecordId, {
      ...shippingRecord,
      ...values,
    });
    await queryClient.invalidateQueries({
      queryKey: [CacheInvalidationAction.GET_SHIPPING_RECORD, shippingRecordId],
    });
    fetchShippingRecord(shippingRecordId, { force: true });
  };

  const handleRemoveShipment = useCallback(
    async (shipmentId: string) => {
      if (
        isShippingRecordCanceled ||
        shippingRecord?.status !== ShippingRecordStatus.TO_SHIP
      ) {
        return;
      }
      await ShippingRecordService.removeShipmentFromShippingRecord(
        shippingRecordId,
        shipmentId
      );
      await queryClient.invalidateQueries({
        queryKey: [
          CacheInvalidationAction.GET_OPS_WITH_PARCEL_AND_NO_SHIPPING_RECORD,
        ],
      });
      await queryClient.invalidateQueries({
        queryKey: [
          CacheInvalidationAction.GET_SHIPPING_RECORD,
          shippingRecordId,
        ],
      });
      fetchShippingRecord(shippingRecordId, { force: true });
    },
    [
      fetchShippingRecord,
      isShippingRecordCanceled,
      shippingRecord?.status,
      shippingRecordId,
    ]
  );

  return (
    <div className={jclasses.jWrapper}>
      <Layout
        renderHead={() => (
          <HeaderShippingRecordsDetails
            shippingRecordId={shippingRecordId}
            shippingRecord={shippingRecord}
            updateShippingRecord={updateShippingRecord}
            isFetchingShippingRecord={isFetchingShippingRecord}
            toggleHistoryDrawer={toggleHistoryDrawer}
            toggleCancelShippingRecordModal={toggleCancelShippingRecordModal}
            isShippingRecordCanceled={isShippingRecordCanceled}
          />
        )}
        title={shippingRecord?.code}
        contentClassName={classes.mainContainer}
      >
        <>
          <div className={classes.shippingLocationRow}>
            <ShippingDeliveryDatesBlock
              shippingRecord={shippingRecord}
              updateShippingRecord={handleShippingRecordUpdate}
              disabled={isShippingRecordCanceled}
              classes={classes}
            />
            <ShippingLocationInfoBlock
              shippingRecord={shippingRecord}
              classes={classes}
            />
          </div>

          <ShippingParcelInfoBlock
            shippingRecord={shippingRecord}
            classes={classes}
            onRemoveShipment={handleRemoveShipment}
          />
          <div className={classes.lineBlockContainer}>
            <ShippingRecordInfoBlock
              shippingRecord={shippingRecord}
              classes={classes}
              updateShippingRecord={handleShippingRecordUpdate}
              disabled={isShippingRecordCanceled}
            />
            <div className={classes.lineBlockChildContainer}>
              <ShippingRecordFileUploadBlock
                shippingRecord={shippingRecord}
                classes={classes}
                updateShippingRecord={handleShippingRecordUpdate}
                fetchShippingRecord={() =>
                  fetchShippingRecord(shippingRecordId)
                }
                disabled={isShippingRecordCanceled}
              />
            </div>
          </div>
          {shippingRecord?.deliveryType !== DeliveryType.PROVISIONING && (
            <ShippingRecordDeliveryTrackingBlock
              shippingRecord={shippingRecord}
              classes={classes}
              fetchShippingRecord={() => fetchShippingRecord(shippingRecordId)}
              disabled={isShippingRecordCanceled}
            />
          )}
          <ShippingRecordDisputeTableBlock
            shippingRecord={shippingRecord}
            fetchShippingRecord={() => fetchShippingRecord(shippingRecordId)}
            classes={classes}
            disabled={isShippingRecordCanceled}
          />
        </>
      </Layout>
      <ShippingRecordHistoryPopin
        isOpen={isHistoryDrawerOpen}
        onClose={() => toggleHistoryDrawer(false)}
        shippingRecord={shippingRecord}
      />
      <Modal
        title="Annulation du dossier"
        textValidate="Oui"
        textCancel="Non"
        open={isCancelShippingRecordModalOpen}
        onValidate={() => {
          handleShippingRecordUpdate({ status: ShippingRecordStatus.CANCELED });
          toggleCancelShippingRecordModal(false);
        }}
        onCancel={() => toggleCancelShippingRecordModal(false)}
      >
        <div className={jclasses.dialogContent} style={{ textAlign: "center" }}>
          <Text>
            {`Vous êtes sur le point d’annuler le dossier, par conséquent 
            tous les OP seront de nouveau disponible(s) sur la vue “Expédition > Traitement” 
            et vous pourrez les remettre dans un nouveau dossier.`}
            <br />
            <br />
            <b>À noter</b> : Le dossier ne sera pas annulé auprès du
            transporteur, veuillez prendre contact avec lui.
            <br />
            <br />
            Etes-vous sûr de vouloir continuer ?
          </Text>
        </div>
      </Modal>
    </div>
  );
}

export default ShippingRecordDetails;
